import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { motion, AnimatePresence } from 'framer-motion';
import { colors } from '../theme';
import { springSnappy } from '../utils/animations';
import { handleSubmission, ensureAppCheck } from '../config/firebase-config';
import TypingDots from './TypingDots';
import TypingMessage from './TypingMessage';
import { track } from '../utils/analytics';

const scrollToBottom = (ref) => {
  if (ref.current) {
    ref.current.scrollTop = ref.current.scrollHeight;
  }
};

const STARTERS = [
  'What kinds of projects do you take on?',
  'How does an engagement start?',
  'Can you work with our ERP?',
];

const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [input, setInput] = useState('');
  const [chat, setChat] = useState([
    {
      from: 'bot',
      text: "Hi there! I'm Minne-Bot. You can ask me anything about what we do, our services, or whether we'd be a good fit for your project.",
      typing: true,
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  const chatRef = useRef(null);
  const contentRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const closedByUser = useRef(false);

  const closeChat = () => {
    track('chat_close', { messages: chat.filter((m) => m.from === 'user').length });
    closedByUser.current = true;
    setOpen(false);
  };

  // After a close, hand focus back to the button that reopens the panel.
  useEffect(() => {
    if (open || !closedByUser.current) return;
    closedByUser.current = false;
    fabRef.current?.focus({ preventScroll: true });
  }, [open]);

  // Keyboard users land in the panel, not back on the body.
  useEffect(() => {
    if (open) inputRef.current?.focus({ preventScroll: true });
  }, [open]);

  // Show teaser after hero animation fully loads
  useEffect(() => {
    if (dismissed) return;
    const timer = setTimeout(() => {
      if (!open && !dismissed) setTeaser(true);
    }, 12000);
    return () => clearTimeout(timer);
  }, [open, dismissed]);

  useEffect(() => {
    if (!open || !contentRef.current) return;
    const ro = new ResizeObserver(() => scrollToBottom(chatRef));
    ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [open]);

  const sendMessage = async (message) => {
    setLoading(true);

    try {
      await ensureAppCheck();
      const result = await handleSubmission({ message, conversationId });
      const replyObj = result.data?.reply;

      if (result.data?.conversationId && !conversationId) {
        setConversationId(result.data.conversationId);
      }

      if (replyObj && replyObj.content) {
        if (/taken down your information/i.test(replyObj.content)) track('chat_lead');
        return replyObj.content;
      } else {
        return "Sorry, I didn't get that. Can you rephrase?";
      }
    } catch (error) {
      console.error('Error calling handleSubmission:', error);
      track('chat_error', { code: error?.code || 'unknown' });
      // The function sends a visitor-facing message when the model is down.
      if (error?.code === 'functions/unavailable' && error.message) return error.message;
      return 'Something went wrong. Please try again.';
    } finally {
      setLoading(false);
    }
  };

  const submitMessage = async (userMessage) => {
    const turn = chat.filter((m) => m.from === 'user').length + 1;
    track('chat_message', { turn });
    setChat((prev) => [...prev, { from: 'user', text: userMessage }]);
    setInput('');

    const botReply = await sendMessage(userMessage);
    setChat((prev) => [...prev, { from: 'bot', text: botReply, typing: true }]);
  };

  const handleSend = () => {
    if (!input.trim()) return;
    submitMessage(input.trim());
  };

  const handleStarter = (text) => {
    track('chat_starter', { prompt: text });
    submitMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenChat = () => {
    ensureAppCheck(); // reCAPTCHA loads while they type their first message
    track('chat_open');
    setTeaser(false);
    setDismissed(true);
    setOpen(true);
  };

  const handleDismissTeaser = (e) => {
    e.stopPropagation();
    track('chat_teaser_dismiss');
    setTeaser(false);
    setDismissed(true);
  };

  return (
    <>
      <AnimatePresence>
        {/* Teaser bubble */}
        {teaser && !open && !dismissed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={springSnappy}
            style={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1100,
              cursor: 'pointer',
            }}
            onClick={handleOpenChat}
            role="button"
            tabIndex={0}
            aria-label="Open chat assistant"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenChat(); }
            }}
          >
            <Paper
              elevation={8}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0, sm: 1.5 },
                px: { xs: 1.75, sm: 2 },
                py: { xs: 1.75, sm: 1.25 },
                borderRadius: '999px',
                background: `linear-gradient(135deg, ${colors.green}, ${colors.greenDark})`,
                color: 'white',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: '1.3rem' }} />
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.2 }}>
                  Questions?
                </Typography>
              </Box>
              <IconButton
                onClick={handleDismissTeaser}
                aria-label="Dismiss"
                size="small"
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'rgba(255,255,255,0.7)', ml: 0.5, p: 1, my: -0.5, minWidth: 36, minHeight: 36, '&:hover': { color: 'white' } }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Original chat button (after teaser dismissed or closed) */}
      {!open && !teaser && dismissed && (
        <IconButton
          ref={fabRef}
          onClick={handleOpenChat}
          aria-label="Open chat assistant"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            bgcolor: colors.green,
            color: 'white',
            '&:hover': { bgcolor: colors.greenDark },
            '&.Mui-focusVisible': { outline: `2px solid ${colors.green}`, outlineOffset: 3 },
            boxShadow: 3,
            zIndex: 1100,
          }}
          size="large"
        >
          <ChatBubbleOutlineIcon fontSize="inherit" />
        </IconButton>
      )}

      {open && (
        <Paper
          elevation={6}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); closeChat(); } }}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            width: 320,
            height: 420,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 6,
            zIndex: 1100,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              bgcolor: colors.green,
              color: 'white',
              p: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              Minne-Bot
            </Typography>
            <IconButton
              onClick={closeChat}
              aria-label="Close chat"
              sx={{ color: 'white' }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            ref={chatRef}
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 1,
              bgcolor: 'grey.100',
              fontSize: '0.9rem',
            }}
          >
            <Box ref={contentRef}>
            {chat.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '90%',
                    bgcolor: msg.from === 'user' ? 'primary.light' : 'grey.300',
                    color: msg.from === 'user' ? 'primary.contrastText' : 'text.primary',
                    p: 1.2,
                    borderRadius: 2,
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                  }}
                >
                  {msg.from === 'bot' && msg.typing ? (
                    <TypingMessage
                      text={msg.text}
                      speed={20}
                      onComplete={() => {
                        setChat((prev) =>
                          prev.map((m, idx) => idx === i ? { ...m, typing: false } : m)
                        );
                      }}
                    />
                  ) : (
                    msg.text
                  )}
                </Box>
              </Box>
            ))}

            {/* A few ways in, shown until the first question is asked. */}
            {chat.length === 1 && !chat[0].typing && !loading && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5, mb: 1, px: 0.5 }}>
                {STARTERS.map((s) => (
                  <Box
                    key={s}
                    component="button"
                    type="button"
                    onClick={() => handleStarter(s)}
                    sx={{
                      font: 'inherit', fontSize: '0.82rem', cursor: 'pointer',
                      px: 1.25, py: 0.6, borderRadius: '999px',
                      border: `1px solid ${colors.green}`, color: colors.greenText, bgcolor: 'white',
                      '&:hover': { bgcolor: colors.green, color: 'white' },
                    }}
                  >
                    {s}
                  </Box>
                ))}
              </Box>
            )}

            {loading && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '90%',
                    bgcolor: 'grey.300',
                    color: 'text.primary',
                    p: 1.2,
                    borderRadius: 2,
                  }}
                >
                  <TypingDots />
                </Box>
              </Box>
            )}
            </Box>
          </Box>

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            sx={{ p: 1, borderTop: '1px solid', borderColor: 'grey.300' }}
          >
            <TextField
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              variant="outlined"
              size="small"
              inputRef={inputRef}
            />
            <Typography sx={{ mt: 0.5, px: 0.5, fontSize: '0.66rem', color: 'text.secondary', lineHeight: 1.5 }}>
              Protected by reCAPTCHA. Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>Privacy</a>
              {' '}&amp;{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>Terms</a>
              {' '}apply.
            </Typography>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ChatAssistant;
